/** Read-only browse of the normalized store (layer B): parts, OE codes, links. */
import { useState, type ReactNode } from 'react';
import { useList } from '../lib/hooks';
import { qs, fmtTime } from '../lib/util';
import { Banner, DataBlock, Empty, ErrorBox, Loading, Pager, SourceChip, Table, Toolbar } from '../ui';

type Kind = 'parts' | 'oe-codes' | 'links';
type Row = Record<string, unknown>;
const mono = (v: unknown): ReactNode => <span className="mono">{v == null || v === '' ? '—' : String(v)}</span>;

interface Cfg {
  title: string;
  path: string;
  banner: ReactNode;
  filters: { key: string; label: string }[];
  cols: string[];
  cells: (r: Row) => ReactNode[];
}

const CFG: Record<Kind, Cfg> = {
  parts: {
    title: 'Parts',
    path: '/v1/admin/parts',
    banner: 'Normalized part store — one row per (brand, article) built asynchronously from cached responses.',
    filters: [
      { key: 'brand', label: 'Brand' },
      { key: 'article', label: 'Article' },
    ],
    cols: ['Brand', 'Article', 'Brand raw', 'Article raw', 'Name'],
    cells: (r) => [mono(r.brandNorm), mono(r.articleNorm), String(r.brandRaw ?? '—'), String(r.articleRaw ?? '—'), String(r.name ?? '—')],
  },
  'oe-codes': {
    title: 'OE codes',
    path: '/v1/admin/oe-codes',
    banner: 'Normalized OE codes seen across sources.',
    filters: [{ key: 'code', label: 'OE code' }],
    cols: ['OE (normalized)', 'OE raw'],
    cells: (r) => [mono(r.codeNorm), String(r.codeRaw ?? '—')],
  },
  links: {
    title: 'OE ↔ part links',
    path: '/v1/admin/links',
    banner: 'Which source linked which OE code to which part, and when.',
    filters: [{ key: 'source', label: 'Source' }],
    cols: ['OE', 'Brand', 'Article', 'Source', 'Source id', 'Last seen'],
    cells: (r) => [
      mono(r.oeNorm),
      String(r.brandNorm ?? '—'),
      mono(r.articleNorm),
      <SourceChip key="s" source={String(r.source ?? '')} />,
      mono(r.sourceId),
      fmtTime(String(r.lastSeen ?? '')),
    ],
  },
};

const LIMIT = 50;

export default function Normalized({ kind }: { kind: Kind }) {
  const cfg = CFG[kind];
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const path = `${cfg.path}${qs({ ...filters, limit: LIMIT, offset })}`;
  const { data, loading, error } = useList<Row>(path);

  const set = (key: string, val: string) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setOffset(0);
  };

  const items = data?.items ?? [];
  return (
    <>
      <Banner icon="▤">{cfg.banner}</Banner>
      <Toolbar>
        {cfg.filters.map((f) => (
          <input
            key={f.key}
            className="input mono"
            style={{ width: 200 }}
            placeholder={f.label}
            value={filters[f.key] ?? ''}
            onChange={(e) => set(f.key, e.target.value)}
          />
        ))}
        {data && (
          <Pager total={data.total} limit={LIMIT} offset={offset} onOffset={setOffset} />
        )}
      </Toolbar>
      {error && <ErrorBox>{error}</ErrorBox>}
      <DataBlock title={cfg.title} sub={data ? `${data.total} total` : undefined} raw={data}>
        {loading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Empty />
        ) : (
          <Table cols={cfg.cols}>
            {items.map((r, i) => (
              <tr key={i}>
                {cfg.cells(r).map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </Table>
        )}
      </DataBlock>
    </>
  );
}
